import { useEffect, useRef, useState, type ReactNode } from 'react';

import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/seo/Seo';
import PersonalLeadSection from './components/PersonalLeadSection';
import AboutSection from '../home/components/AboutSection';
import CoursesSection from '../home/components/CoursesSection';
import CurriculumSection from '../home/components/CurriculumSection';
import BenefitsSection from '../home/components/BenefitsSection';
import FacilityAccessSection from '../home/components/FacilityAccessSection';
import HomeFAQSection from '../home/components/HomeFAQSection';
import FinalCTASection from '../home/components/FinalCTASection';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function PersonalPage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();

  const serviceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: t('personal.jsonld.serviceName'),
    provider: {
      '@type': 'Organization',
      name: t('personal.jsonld.orgName'),
    },
    serviceType: t('personal.jsonld.serviceType'),
    areaServed: {
      '@type': 'Country',
      name: lang === 'en' ? 'Japan' : t('personal.jsonld.areaServed'),
    },
    description: t('personal.jsonld.description'),
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('personal.jsonld.breadcrumb1'), item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: t('personal.jsonld.breadcrumb2'), item: 'https://butler-academy.jp/personal' },
    ],
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/personal" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceStructuredData) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
        />

        <PersonalLeadSection />

        <Breadcrumb items={[{ label: '個人のお客様（通学型コース）', labelEn: 'For Individuals (On-Campus Courses)' }]} />

        <FadeInSection>
          <AboutSection />
        </FadeInSection>

        <div id="courses">
          <FadeInSection>
            <CoursesSection />
          </FadeInSection>
        </div>

        <FadeInSection>
          <CurriculumSection />
        </FadeInSection>

        <FadeInSection>
          <BenefitsSection />
        </FadeInSection>

        <FadeInSection>
          <FacilityAccessSection />
        </FadeInSection>

        <FadeInSection>
          <HomeFAQSection />
        </FadeInSection>

        {/* Corporate cross-link */}
        <div className="bg-background-100 py-10 text-center">
          {String(t('personal.cta.corporateNote')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-600 tracking-[0.04em] mb-4">{__s}</p>))}
          <Link
            to="/corporate"
            className="inline-flex items-center gap-3 text-accent-600 hover:text-accent-500 text-[15px] tracking-[0.12em] transition-colors duration-300 group"
          >
            <span>{t('personal.cta.corporateLink')}</span>
            <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
          </Link>
        </div>

        <FadeInSection>
          <FinalCTASection />
        </FadeInSection>
      </main>
      <Footer />
    </>
  );
}
